import React, { useState } from "react";
import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import {
  LuCircleAlert,
  LuCircleCheck,
  LuClock,
  LuFacebook,
  LuGithub,
  LuLinkedin,
  LuMail,
  LuMapPin,
  LuPhone,
  LuSend,
} from "react-icons/lu";
import {
  Button,
  Container,
  Eyebrow,
  IconBox,
  Lead,
  Reveal,
  Section,
  SectionTitle,
} from "./ui";

const contactDetails = [
  {
    icon: <LuMail />,
    label: "Email",
    value: "ussefbelkhiraoui@gmail.com",
    link: "mailto:ussefbelkhiraoui@gmail.com",
  },
  {
    icon: <LuPhone />,
    label: "Phone",
    value: "+212 660-577967",
    link: "tel:+212660577967",
  },
  {
    icon: <LuMapPin />,
    label: "Office",
    value: "Rabat, Morocco",
    link: null,
  },
];

const socialLinks = [
  {
    icon: <LuLinkedin />,
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/code-solutions1",
  },
  { icon: <LuGithub />, label: "GitHub", href: "https://github.com/ussef11" },
  {
    icon: <LuFacebook />,
    label: "Facebook",
    href: "https://web.facebook.com/people/Code-Solutions/61582424098626/",
  },
];

const emptyForm = { name: "", email: "", subject: "", message: "" };

const Layout = styled(Container)`
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 64px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const IntroLead = styled(Lead)`
  margin-top: 20px;
`;

const Details = styled.ul`
  display: grid;
  gap: 12px;
  margin: 40px 0 32px;
`;

const Detail = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;

  small {
    display: block;
    font-size: 0.8125rem;
    color: var(--color-muted);
  }

  a,
  span {
    font-weight: 500;
    color: var(--color-ink);
    word-break: break-word;
  }

  a:hover {
    color: var(--color-primary);
  }
`;

const ResponseNote = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-radius: var(--radius);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  font-size: 0.9375rem;
  color: var(--color-text);

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: var(--color-primary);
  }
`;

const Socials = styled.ul`
  display: flex;
  gap: 8px;
  margin-top: 32px;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  transition: border-color 0.2s ease, color 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    border-color: var(--color-ink);
    color: var(--color-ink);
  }
`;

const FormCard = styled(Reveal)`
  padding: 40px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);

  h3 {
    font-size: 1.5rem;
    margin-bottom: 6px;
  }

  > p {
    color: var(--color-muted);
    margin-bottom: 32px;
  }

  @media (max-width: 640px) {
    padding: 24px 20px;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ $full }) =>
    $full &&
    css`
      grid-column: 1 / -1;
    `}

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--color-ink);
  }

  label span {
    color: var(--color-muted);
    font-weight: 400;
  }
`;

const inputStyles = css`
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius);
  background: var(--color-surface);
  font-size: 1rem;
  color: var(--color-ink);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #8a96a8;
  }

  &:hover {
    border-color: var(--color-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(30, 68, 240, 0.18);
  }
`;

const Input = styled.input`
  ${inputStyles}
`;

const TextArea = styled.textarea`
  ${inputStyles}
  min-height: 140px;
  resize: vertical;
`;

const FormFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 28px;

  small {
    flex: 1 1 260px;
    font-size: 0.8125rem;
    line-height: 1.55;
    color: var(--color-muted);
  }

  small a {
    color: var(--color-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  @media (max-width: 640px) {
    button {
      width: 100%;
    }
  }
`;

const Status = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  padding: 14px 16px;
  border-radius: var(--radius);
  font-size: 0.9375rem;
  background: ${({ $type }) =>
    $type === "success" ? "var(--color-success-soft)" : "var(--color-danger-soft)"};
  color: ${({ $type }) =>
    $type === "success" ? "var(--color-success)" : "var(--color-danger)"};

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-top: 1px;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      const apiUrl =
        process.env.REACT_APP_NODE_ENV === "production"
          ? process.env.REACT_APP_API_URL + "/api/contact"
          : "http://localhost:5000/api/contact";

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setStatus({
          type: "success",
          message: "Thank you for your message! We'll get back to you within 24 hours.",
        });
        setFormData(emptyForm);
      } else {
        setStatus({
          type: "error",
          message: result.message || "Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setStatus({
        type: "error",
        message:
          "We couldn't send your message. Please check your connection and try again, or email us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section id="contact" $tone="subtle">
      <Layout>
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <SectionTitle>Let's work together</SectionTitle>
          <IntroLead>
            Have a project in mind? Tell us about it and we'll discuss how we
            can bring your idea to life.
          </IntroLead>

          <Details>
            {contactDetails.map(detail => (
              <Detail key={detail.label}>
                <IconBox aria-hidden="true">{detail.icon}</IconBox>
                <div>
                  <small>{detail.label}</small>
                  {detail.link ? (
                    <a href={detail.link}>{detail.value}</a>
                  ) : (
                    <span>{detail.value}</span>
                  )}
                </div>
              </Detail>
            ))}
          </Details>

          <ResponseNote>
            <LuClock aria-hidden="true" />
            We reply to every inquiry within 24 hours.
          </ResponseNote>

          <Socials>
            {socialLinks.map(social => (
              <li key={social.label}>
                <SocialLink
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                >
                  {social.icon}
                </SocialLink>
              </li>
            ))}
          </Socials>
        </Reveal>

        <FormCard forwardedAs="form" delay={0.1} onSubmit={handleSubmit}>
          <h3>Send us a message</h3>
          <p>Fill out the form and our team will get back to you.</p>

          <FieldGrid>
            <Field>
              <label htmlFor="name">Name</label>
              <Input
                type="text"
                id="name"
                name="name"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                required
              />
            </Field>

            <Field>
              <label htmlFor="email">Email</label>
              <Input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                required
              />
            </Field>

            <Field $full>
              <label htmlFor="subject">Subject</label>
              <Input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="e.g. New mobile app"
                required
              />
            </Field>

            <Field $full>
              <label htmlFor="message">Message</label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your project, goals and timeline…"
                required
              />
            </Field>
          </FieldGrid>

          <FormFooter>
            <small>
              All fields are required. We use your details only to reply to
              your request, as described in our{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>.
            </small>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending…" : "Send message"}
              <LuSend aria-hidden="true" />
            </Button>
          </FormFooter>

          <div role="status" aria-live="polite">
            {status && (
              <Status $type={status.type}>
                {status.type === "success" ? (
                  <LuCircleCheck aria-hidden="true" />
                ) : (
                  <LuCircleAlert aria-hidden="true" />
                )}
                {status.message}
              </Status>
            )}
          </div>
        </FormCard>
      </Layout>
    </Section>
  );
};

export default Contact;
